import { useRef } from 'react';
import { TOKEN_SPLIT_RE } from './HighlightedTemplate';
import type { TemplateTokenKind } from './tool-inputs';
import styles from './ToolForm.module.css';

const TOKEN_KIND_CLASSES: Record<TemplateTokenKind, string> = {
  valid: styles.templateTokenValid,
  invalid: styles.templateTokenInvalid,
  neutral: styles.templateTokenNeutral,
};

interface TemplateInputProps {
  value: string;
  onChange: (next: string) => void;
  classifyToken: (token: string) => TemplateTokenKind;
  placeholder?: string;
}

/**
 * A mono text input that highlights `{{...}}` tokens in place, colored by whether the reference
 * resolves. The input's own text is transparent over an identically-metriced backdrop that renders
 * the highlighted copy, so typing, selection, and the caret behave like a plain input.
 */
export const TemplateInput = ({ value, onChange, classifyToken, placeholder }: TemplateInputProps) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const segments = value.split(TOKEN_SPLIT_RE);

  const syncBackdropScroll = (input: HTMLInputElement) => {
    if (backdropRef.current) backdropRef.current.scrollLeft = input.scrollLeft;
  };

  return (
    <div className={styles.templateInput}>
      <div ref={backdropRef} className={styles.templateInputBackdrop} aria-hidden>
        {segments.map((segment, index) =>
          segment.startsWith('{{') && segment.endsWith('}}') ? (
            <span key={index} className={TOKEN_KIND_CLASSES[classifyToken(segment)]}>
              {segment}
            </span>
          ) : (
            segment
          ),
        )}
      </div>
      <input
        type="text"
        value={value}
        onChange={e => {
          onChange(e.target.value);
          syncBackdropScroll(e.target);
        }}
        onScroll={e => syncBackdropScroll(e.currentTarget)}
        className={styles.templateInputControl}
        placeholder={placeholder}
        spellCheck={false}
      />
    </div>
  );
};
